// ======================== ADMIN RESET PAGE ========================
// Page de remise à zéro des données de test - ADMIN uniquement

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertTriangle, Trash2, Archive, RefreshCw, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminResetPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmCode, setConfirmCode] = useState('');
  const [options, setOptions] = useState({
    reset_daily_stats: true,
    reset_today_orders: true,
    reset_kitchen_bar_states: true,
    reset_sessions: false,
    reset_stocks_to_initial: false,
    reset_test_data_only: true,
    archive_before_delete: true
  });
  const [result, setResult] = useState(null);
  
  // Vérifier que c'est un admin
  useEffect(() => {
    if (user?.role !== 'ADMIN') {
      toast.error('Accès réservé à l\'administrateur');
      navigate('/dashboard');
    }
  }, [user, navigate]);
  
  // Charger la prévisualisation
  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await api().get('/admin/reset/preview');
      setPreview(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la prévisualisation');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadPreview();
  }, []);
  
  // Exécuter la remise à zéro
  const executeReset = async () => {
    if (confirmCode !== 'RESET-TEST-DATA') {
      toast.error('Code de confirmation incorrect');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api().post('/admin/reset/execute', {
        confirmation_code: confirmCode,
        options
      });
      
      setResult(res.data);
      setConfirming(false);
      setConfirmCode('');
      toast.success('Remise à zéro effectuée avec succès');
      
      // Recharger la prévisualisation
      setTimeout(loadPreview, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la remise à zéro');
    } finally {
      setLoading(false);
    }
  };
  
  if (user?.role !== 'ADMIN') return null;
  
  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <ShieldAlert className="w-8 h-8 text-red-500" />
        <div>
          <h1 className="text-2xl font-bold">Remise à Zéro des Données</h1>
          <p className="text-muted-foreground">Réinitialiser l'environnement de test</p>
        </div>
      </div>
      
      {/* Alerte de danger */}
      <Card className="mb-6 border-red-500 bg-red-50 dark:bg-red-900/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-700 dark:text-red-400">
                Action irréversible
              </p>
              <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                Cette action supprimera les données sélectionnées. Les données seront archivées avant suppression si l'option est activée.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Prévisualisation */}
      {preview && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Données actuelles ({preview.preview?.date})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{preview.preview?.orders_today || 0}</p>
                <p className="text-xs text-muted-foreground">Commandes du jour</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{preview.preview?.orders_paid_today || 0}</p>
                <p className="text-xs text-muted-foreground">Payées</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{(preview.preview?.total_sales_today || 0).toLocaleString()} F</p>
                <p className="text-xs text-muted-foreground">CA du jour</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{preview.preview?.open_sessions || 0}</p>
                <p className="text-xs text-muted-foreground">Sessions ouvertes</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{preview.preview?.kitchen_tickets_active || 0}</p>
                <p className="text-xs text-muted-foreground">Tickets cuisine</p>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{preview.preview?.bar_tickets_active || 0}</p>
                <p className="text-xs text-muted-foreground">Tickets bar</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Options */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Options de remise à zéro</CardTitle>
          <CardDescription>Sélectionnez ce que vous souhaitez réinitialiser</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="reset_daily_stats"
              checked={options.reset_daily_stats}
              onCheckedChange={(checked) => setOptions(prev => ({ ...prev, reset_daily_stats: checked }))}
            />
            <Label htmlFor="reset_daily_stats" className="flex-1">
              <span className="font-medium">CA du jour</span>
              <span className="text-xs text-muted-foreground block">Remettre le chiffre d'affaires journalier à zéro</span>
            </Label>
          </div>
          
          <div className="flex items-center space-x-3">
            <Checkbox
              id="reset_today_orders"
              checked={options.reset_today_orders}
              onCheckedChange={(checked) => setOptions(prev => ({ ...prev, reset_today_orders: checked }))}
            />
            <Label htmlFor="reset_today_orders" className="flex-1">
              <span className="font-medium">Commandes du jour</span>
              <span className="text-xs text-muted-foreground block">Supprimer toutes les commandes créées aujourd'hui</span>
            </Label>
          </div>
          
          <div className="flex items-center space-x-3">
            <Checkbox
              id="reset_kitchen_bar_states"
              checked={options.reset_kitchen_bar_states}
              onCheckedChange={(checked) => setOptions(prev => ({ ...prev, reset_kitchen_bar_states: checked }))}
            />
            <Label htmlFor="reset_kitchen_bar_states" className="flex-1">
              <span className="font-medium">États cuisine/bar</span>
              <span className="text-xs text-muted-foreground block">Réinitialiser les statuts de préparation</span>
            </Label>
          </div>
          
          <div className="flex items-center space-x-3">
            <Checkbox
              id="reset_sessions"
              checked={options.reset_sessions}
              onCheckedChange={(checked) => setOptions(prev => ({ ...prev, reset_sessions: checked }))}
            />
            <Label htmlFor="reset_sessions" className="flex-1">
              <span className="font-medium text-orange-600">Sessions de caisse</span>
              <span className="text-xs text-muted-foreground block">Fermer toutes les sessions ouvertes (attention: déconnecte les caissiers)</span>
            </Label>
          </div>
          
          <div className="flex items-center space-x-3">
            <Checkbox
              id="reset_stocks_to_initial"
              checked={options.reset_stocks_to_initial}
              onCheckedChange={(checked) => setOptions(prev => ({ ...prev, reset_stocks_to_initial: checked }))}
            />
            <Label htmlFor="reset_stocks_to_initial" className="flex-1">
              <span className="font-medium text-orange-600">Restaurer les stocks</span>
              <span className="text-xs text-muted-foreground block">Annuler les mouvements de stock du jour (remet les quantités)</span>
            </Label>
          </div>
          
          <hr className="my-4" />
          
          <div className="flex items-center space-x-3">
            <Checkbox
              id="archive_before_delete"
              checked={options.archive_before_delete}
              onCheckedChange={(checked) => setOptions(prev => ({ ...prev, archive_before_delete: checked }))}
            />
            <Label htmlFor="archive_before_delete" className="flex-1">
              <span className="font-medium text-green-600">Archiver avant suppression</span>
              <span className="text-xs text-muted-foreground block">Sauvegarder les données avant de les supprimer (recommandé)</span>
            </Label>
          </div>
        </CardContent>
      </Card>
      
      {/* Résultat */}
      {result && (
        <Card className="mb-6 border-green-500 bg-green-50 dark:bg-green-900/20">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Remise à zéro effectuée
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-2">
              Exécuté le {new Date(result.results?.executed_at).toLocaleString('fr-FR')} par {result.results?.executed_by}
            </p>
            <ul className="list-disc list-inside text-sm space-y-1">
              {result.results?.actions?.map((action, i) => (
                <li key={i}>{action}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      
      {/* Boutons d'action */}
      {!confirming ? (
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={loadPreview}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          
          <Button
            variant="destructive"
            onClick={() => setConfirming(true)}
            disabled={loading}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Remettre à zéro
          </Button>
        </div>
      ) : (
        <Card className="border-red-500">
          <CardHeader>
            <CardTitle className="text-lg text-red-600">Confirmation requise</CardTitle>
            <CardDescription>
              Tapez <code className="bg-muted px-1 py-0.5 rounded font-mono">RESET-TEST-DATA</code> pour confirmer
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Input
              type="text"
              placeholder="Entrez le code de confirmation"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value.toUpperCase())}
              className="font-mono"
              data-testid="reset-confirmation-input"
            />
          </CardContent>
          <CardFooter className="flex gap-4">
            <Button
              variant="outline"
              onClick={() => {
                setConfirming(false);
                setConfirmCode('');
              }}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={executeReset}
              disabled={loading || confirmCode !== 'RESET-TEST-DATA'}
              data-testid="confirm-reset-button"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Confirmer la remise à zéro
            </Button>
          </CardFooter>
        </Card>
      )}
    </div>
  );
};

export default AdminResetPage;
